import {
  BoundingSphere,
  Cartesian2,
  Cartesian3,
  Math as CesiumMath,
  Color,
  ColorMaterialProperty,
  HeadingPitchRange,
  HeightReference,
  Rectangle,
  VerticalOrigin,
} from "cesium";
import { useEffect, useRef, useState } from "react";
import { parseGeoData } from "senf-shared";
import { flyToFeature } from "../mapBuilder/components/cesium/cesium-camera";
import {
  filterGisPointFeatures,
  setGisFeaturesOpacity,
  showAllGisPointFeatures,
} from "../utils/cesiumGisUtils";

interface PinData {
  ideaId?: string;
  long?: number;
  lat?: number;
  height?: number;
  color?: string;
  geoData?: any;
}

interface UseIdeaPinCesiumProps {
  cesiumViewer: any; // Cesium.Viewer instance
  pinData: PinData | null;
  setIdeaMarkerColor: (color: string) => void;
  paddings?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  setGisData: (data: any[]) => void;
  normalizedGisData: any[];
}

// Function to generate IdeaPin SVG as data URI with dynamic color
const generateIdeaPinDataUri = (color = "#000000"): string => {
  const svgString = `
    <svg width="43" height="51" viewBox="0 0 43 51" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_ddd_1129_15128)">
        <circle cx="21.5" cy="40.0283" r="1.97167" fill="black" fill-opacity="0.01" shape-rendering="crispEdges" />
      </g>
      <g filter="url(#filter1_f_1129_15128)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M18.5 40L35.5 26.5L23.5 42.5C22 44.5 18.5 42.7223 18.5 40Z" fill="#867C63" fill-opacity="0.5" />
      </g>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M21.5 0C16.2447 0 11.9844 4.26037 11.9844 9.51562C11.9844 14.7709 16.2447 19.0312 21.5 19.0312C26.7552 19.0312 31.0156 14.7709 31.0156 9.51562C31.0156 4.26037 26.7552 0 21.5 0Z" fill="${color}" />
      <path d="M21.5 -1.5C15.4163 -1.5 10.4844 3.43195 10.4844 9.51562H13.4844C13.4844 5.0888 17.0732 1.5 21.5 1.5V-1.5ZM10.4844 9.51562C10.4844 15.5993 15.4163 20.5312 21.5 20.5312V17.5312C17.0732 17.5312 13.4844 13.9424 13.4844 9.51562H10.4844ZM21.5 20.5312C27.5837 20.5312 32.5156 15.5993 32.5156 9.51562H29.5156C29.5156 13.9424 25.9268 17.5312 21.5 17.5312V20.5312ZM32.5156 9.51562C32.5156 3.43195 27.5837 -1.5 21.5 -1.5V1.5C25.9268 1.5 29.5156 5.0888 29.5156 9.51562H32.5156Z" fill="black" fill-opacity="0.08" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M19.2031 9.84375C17.573 9.84375 16.25 8.52075 16.25 6.89062C16.25 5.2605 17.573 3.9375 19.2031 3.9375C20.8333 3.9375 22.1562 5.2605 22.1562 6.89062C22.1562 8.52075 20.8333 9.84375 19.2031 9.84375Z" fill="white" fill-opacity="0.75" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M21.499 41.9998C20.4123 41.9998 19.5303 41.1178 19.5303 40.031V23.6248C19.5303 22.538 20.4123 21.656 21.499 21.656C22.5858 21.656 23.4678 22.538 23.4678 23.6248V40.031C23.4678 41.1178 22.5858 41.9998 21.499 41.9998Z" fill="#867C63" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M21.499 41.9998C20.4123 41.9998 19.5303 41.1178 19.5303 40.031V23.6248C19.5303 22.538 20.4123 21.656 21.499 21.656C22.5858 21.656 23.4678 22.538 23.4678 23.6248V40.031C23.4678 41.1178 22.5858 41.9998 21.499 41.9998Z" fill="white" fill-opacity="0.5" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M21.499 41.9998C20.4123 41.9998 19.5303 41.1178 19.5303 40.031V23.6248C19.5303 22.538 20.4123 21.656 21.499 21.656C22.5858 21.656 23.4678 22.538 23.4678 23.6248V40.031C23.4678 41.1178 22.5858 41.9998 21.499 41.9998Z" stroke="#867C63" stroke-width="1.5" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M21.499 41.9998C20.4123 41.9998 19.5303 41.1178 19.5303 40.031V23.6248C19.5303 22.538 20.4123 21.656 21.499 21.656C22.5858 21.656 23.4678 22.538 23.4678 23.6248V40.031C23.4678 41.1178 22.5858 41.9998 21.499 41.9998Z" stroke="white" stroke-opacity="0.25" stroke-width="1.5" />
      <defs>
        <filter id="filter0_ddd_1129_15128" x="10.5283" y="29.0566" width="21.9434" height="21.9434" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset />
          <feGaussianBlur stdDeviation="4.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.266667 0 0 0 0 0.249524 0 0 0 0 0.206667 0 0 0 1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1129_15128" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset />
          <feGaussianBlur stdDeviation="2.5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.266667 0 0 0 0 0.249524 0 0 0 0 0.206667 0 0 0 0.4 0" />
          <feBlend mode="normal" in2="effect1_dropShadow_1129_15128" result="effect2_dropShadow_1129_15128" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.266667 0 0 0 0 0.249524 0 0 0 0 0.206667 0 0 0 0.5 0" />
          <feBlend mode="normal" in2="effect2_dropShadow_1129_15128" result="effect3_dropShadow_1129_15128" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow_1129_15128" result="shape" />
        </filter>
        <filter id="filter1_f_1129_15128" x="13.5" y="21.5" width="27" height="26.8566" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="2.5" result="effect1_foregroundBlur_1129_15128" />
        </filter>
      </defs>
    </svg>
  `;

  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};

const useIdeaPinCesium = ({
  cesiumViewer,
  pinData,
  setIdeaMarkerColor,
  paddings,
  setGisData,
  normalizedGisData,
}: UseIdeaPinCesiumProps) => {
  const pinBillboardRef = useRef<any>(null);
  const lastFlownPinRef = useRef<string | null>(null);
  const pinDataRef = useRef<PinData | null>(pinData);
  const [flyTrigger, setFlyTrigger] = useState(0);

  // Update the pinDataRef when pinData changes and trigger flying
  useEffect(() => {
    pinDataRef.current = pinData;
    // Increment the trigger to cause the flying effect to run
    setFlyTrigger((prev) => prev + 1);
  }, [pinData]);

  // Function to set selected entity visibility
  const setSelectedEntity = (viewer: any, ideaId?: string) => {
    if (!viewer) return;

    // Get all entities in the viewer
    const entities = viewer.entities.values;

    // If no ideaId, show all entities
    if (!ideaId) {
      entities.forEach((entity: any) => {
        if (entity.ideaId) {
          entity.show = true;
        }
      });
      return;
    }

    // Show only the selected entity
    entities.forEach((entity: any) => {
      if (entity.ideaId) {
        entity.show = entity.ideaId === ideaId;
      }
    });
  };

  // Function to set entity opacity
  const setEntityOpacity = (viewer: any, opacity: number) => {
    if (!viewer) return;

    const entities = viewer.entities.values;
    entities.forEach((entity: any) => {
      if (entity.ideaId) {
        if (entity.polygon) {
          entity.polygon.material = new ColorMaterialProperty(
            Color.WHITE.withAlpha(opacity),
          );
        }
        if (entity.polyline) {
          entity.polyline.material = new ColorMaterialProperty(
            Color.WHITE.withAlpha(opacity),
          );
        }
        if (entity.point) {
          entity.point.color = Color.WHITE.withAlpha(opacity);
        }
      }
    });
  };

  // Function to check if we should fly to this pin
  const shouldFlyToPin = (pin: PinData | null): boolean => {
    if (!pin) return false;

    // If we've never flown to a pin before, we should fly
    if (!lastFlownPinRef.current) return true;

    // If the pin has an ideaId and it's different from the last one we flew to, we should fly
    if (pin.ideaId && pin.ideaId !== lastFlownPinRef.current) return true;

    // If the pin has coordinates and they're different from the last one we flew to, we should fly
    if (pin.long && pin.lat) {
      const lastPin = lastFlownPinRef.current;
      // We can't compare coordinates directly, so we'll just fly if the ideaId is different
      return pin.ideaId !== lastPin;
    }

    return false;
  };

  // Effect to handle pin entity creation and updates
  useEffect(() => {
    if (!cesiumViewer) return;

    const currentPinData = pinDataRef.current;

    // Handle GeoJSON data
    if (currentPinData?.geoData) {
      const parsedGeoData = parseGeoData(currentPinData.geoData);
      setGisData([...normalizedGisData, currentPinData.geoData]);

      // Set entity visibility and opacity for selected feature with geoData
      setSelectedEntity(cesiumViewer, currentPinData.ideaId);
      setGisFeaturesOpacity(cesiumViewer, currentPinData.ideaId); // Highlight this feature, make others semi-transparent
    } else {
      // Remove existing pin if any
      if (pinBillboardRef.current) {
        cesiumViewer.entities.remove(pinBillboardRef.current);
        pinBillboardRef.current = null;
      }

      setSelectedEntity(cesiumViewer);
    }

    // Handle point pin with Billboard
    if (currentPinData?.long && currentPinData?.lat) {
      // Remove existing pin if any
      if (pinBillboardRef.current) {
        cesiumViewer.entities.remove(pinBillboardRef.current);
      }

      // Create new billboard entity
      pinBillboardRef.current = cesiumViewer.entities.add({
        position: Cartesian3.fromDegrees(
          currentPinData.long,
          currentPinData.lat,
          currentPinData.height || 0,
        ),
        billboard: {
          image: generateIdeaPinDataUri(currentPinData.color || "#FF0000"),
          verticalOrigin: VerticalOrigin.BOTTOM,
          scale: 1.0,
          heightReference:
            currentPinData.height !== undefined
              ? HeightReference.NONE
              : HeightReference.CLAMP_TO_GROUND,
          pixelOffset: new Cartesian2(0, 10), // Move 10 pixels down (positive Y is down in screen space)
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        // Add properties for click handling
        properties: {
          ideaId: currentPinData.ideaId,
        },
      });

      setIdeaMarkerColor(currentPinData.color || "#FF0000");

      // Filter out the corresponding point feature from GIS data to avoid duplicate markers
      if (currentPinData.ideaId) {
        filterGisPointFeatures(cesiumViewer, currentPinData.ideaId);
      }

      // Make all GIS features semi-transparent when showing a pin (use a non-existent ID to dim all)
      setGisFeaturesOpacity(cesiumViewer, "__PIN_MODE__");
    } else if (!currentPinData?.geoData) {
      // Only reset when there's no geoData and no pin - means nothing is selected
      if (pinBillboardRef.current) {
        cesiumViewer.entities.remove(pinBillboardRef.current);
        pinBillboardRef.current = null;
      }

      // Reset all features to normal opacity and show all points
      setGisFeaturesOpacity(cesiumViewer); // No ideaId = reset to normal opacity
      showAllGisPointFeatures(cesiumViewer);
    }

    // Cleanup function
    return () => {
      if (pinBillboardRef.current) {
        cesiumViewer.entities.remove(pinBillboardRef.current);
        pinBillboardRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cesiumViewer, setIdeaMarkerColor, setGisData, normalizedGisData]);

  // Separate effect to handle flying to pins
  useEffect(() => {
    if (!cesiumViewer) return;

    const currentPinData = pinDataRef.current;

    // If there's no pin data, reset the last flown pin
    if (!currentPinData) {
      lastFlownPinRef.current = null;
      return;
    }

    // Check if we should fly to this pin
    if (!shouldFlyToPin(currentPinData)) {
      return;
    }

    // Handle flying to GeoJSON data
    if (currentPinData.geoData) {
      // Find the GIS data source (same pattern as cesiumGisUtils.ts)
      const gisDataSource =
        cesiumViewer.dataSources.getByName("gisDataSource")[0];
      console.log("🔍 GIS data source:", gisDataSource);

      if (gisDataSource) {
        // Find all entities with the matching ideaId from the GIS data source
        const matchingEntities = gisDataSource.entities.values.filter(
          (e: any) => {
            const properties = e.properties?.getValue();
            return properties?.ideaId === currentPinData.ideaId;
          },
        );

        console.log("📍 Found matching entities:", matchingEntities.length);

        if (matchingEntities.length > 0) {
          // Prioritize the correct entity type for flying
          // For polygons: prefer the polygon entity over the border polyline
          // For lines: use the polyline entity
          // For points: use the point entity
          let entityToFlyTo = matchingEntities[0]; // Default to first match

          // If there are multiple entities, prioritize polygon over polyline
          const polygonEntity = matchingEntities.find((e) => e.polygon);
          const polylineEntity = matchingEntities.find((e) => e.polyline);
          const pointEntity = matchingEntities.find((e) => e.point);

          if (polygonEntity) {
            entityToFlyTo = polygonEntity; // Prefer polygon for polygon features
          } else if (polylineEntity) {
            entityToFlyTo = polylineEntity; // Use polyline for line features
          } else if (pointEntity) {
            entityToFlyTo = pointEntity; // Use point for point features
          }

          console.log("🛫 Flying to entity:", entityToFlyTo);
          flyToFeature(cesiumViewer, entityToFlyTo);
          // Update the last flown pin
          lastFlownPinRef.current = currentPinData.ideaId || "geoData";
        } else {
          console.log(
            "❌ No entities found with ideaId:",
            currentPinData.ideaId,
          );
        }
      } else {
        console.log("❌ GIS data source not found");
      }
    }

    // Handle flying to point pin
    if (currentPinData.long && currentPinData.lat) {
      if (pinBillboardRef.current) {
        flyToFeature(cesiumViewer, pinBillboardRef.current);
      }

      // Update the last flown pin
      lastFlownPinRef.current =
        currentPinData.ideaId || `${currentPinData.long},${currentPinData.lat}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cesiumViewer, flyTrigger]);

  return true;
};

export default useIdeaPinCesium;
